// src/pages/msme/SigmaStudioRACI.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faPlus,
  faDownload,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";

// Realistic example processes for Flexo / Adhesive Label Printing Factory
const exampleProcesses = [
  {
    id: 1,
    processName: "Customer Order Receipt & Artwork Approval",
    departments: ["Sales", "Design", "Prepress"],
    task: "Receive PO → confirm specs → artwork approval → release to prepress",
    owner: "Sales Manager",
    R: "Sales Executive",
    A: "Sales Manager",
    C: "Customer, Graphic Designer",
    I: "Production Head, Quality Manager",
  },
  {
    id: 2,
    processName: "Prepress – Plate Making",
    departments: ["Prepress"],
    task: "RIP file → CTP plate exposure → plate development → quality check",
    owner: "Prepress Supervisor",
    R: "Plate Technician",
    A: "Prepress Supervisor",
    C: "Graphic Designer",
    I: "Press Operator, Quality Inspector",
  },
  {
    id: 3,
    processName: "Ink Preparation & Mixing",
    departments: ["Ink Room", "Production"],
    task: "Match Pantone / brand colors → viscosity adjustment → ink approval",
    owner: "Ink Technician",
    R: "Ink Room Operator",
    A: "Ink Technician",
    C: "Press Operator, Quality",
    I: "Production Supervisor",
  },
  {
    id: 4,
    processName: "Flexo Press Make-ready & Setup",
    departments: ["Production", "Maintenance"],
    task: "Mount plates → set anilox → web alignment → register & color setup → print approval",
    owner: "Shift In-charge",
    R: "Press Operator",
    A: "Shift In-charge",
    C: "Quality Inspector, Ink Technician",
    I: "Production Head",
  },
  {
    id: 5,
    processName: "Printing & Inline Converting",
    departments: ["Production"],
    task: "Continuous printing → cold foil / varnish / lamination → die-cutting → slitting",
    owner: "Press Operator",
    R: "Press Assistant",
    A: "Press Operator",
    C: "Quality Inspector",
    I: "Shift In-charge, Production Manager",
  },
  {
    id: 6,
    processName: "Quality Control & 100% Inspection",
    departments: ["Quality"],
    task: "Visual + vision system inspection → defect marking → rejection handling",
    owner: "Quality Manager",
    R: "Quality Inspector",
    A: "Quality Manager",
    C: "Production Supervisor",
    I: "Sales, Customer Service",
  },
  {
    id: 7,
    processName: "Rewinding & Finishing",
    departments: ["Production", "Finishing"],
    task: "Rewind inspected rolls → butt splicing → length counting → core labeling",
    owner: "Finishing Supervisor",
    R: "Rewinder Operator",
    A: "Finishing Supervisor",
    C: "Quality",
    I: "Warehouse In-charge",
  },
  {
    id: 8,
    processName: "Packing & Dispatch",
    departments: ["Warehouse", "Logistics"],
    task: "Roll / sheet counting → shrink wrapping → boxing → palletizing → dispatch note",
    owner: "Warehouse Supervisor",
    R: "Packing Operator",
    A: "Warehouse Supervisor",
    C: "Logistics Coordinator",
    I: "Sales, Accounts",
  },
];

// RACI Color Map
const raciColors = {
  R: "bg-green-600",
  A: "bg-blue-600",
  C: "bg-yellow-500",
  I: "bg-gray-500",
};

const SigmaStudioRACI = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showWhyModal, setShowWhyModal] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [processes, setProcesses] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const initialFormData = {
    processName: "",
    departments: "",
    task: "",
    owner: "",
    R: "",
    A: "",
    C: "",
    I: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const handlePopulate = () => {
    if (processes.length > 0) {
      if (!window.confirm("This will replace all current data with flexo label printing examples. Continue?")) {
        return;
      }
    }
    setProcesses(exampleProcesses.map(p => ({ ...p })));
    setCurrentPage(1);
    setSearch("");
  };

  const handleWhyRACI = () => {
    setShowWhyModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this process?")) {
      setProcesses((prev) => prev.filter((p) => p.id !== id));
    }
  };

  const handleEdit = (id) => {
    const process = processes.find((p) => p.id === id);
    if (process) {
      setFormData({
        ...initialFormData,
        processName: process.processName,
        departments: process.departments.join(", "),
        task: process.task,
        owner: process.owner,
        R: process.R,
        A: process.A,
        C: process.C,
        I: process.I,
      });
      setEditingId(id);
      setShowModal(true);
    }
  };

  const handleAdd = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { processName, task, owner, R, A, C, I, departments } = formData;
    const deptArray = departments
      ? departments.split(",").map((d) => d.trim()).filter((d) => d)
      : [];
    const newProcess = {
      id: editingId || Math.max(0, ...processes.map((p) => p.id)) + 1,
      processName,
      departments: deptArray,
      task,
      owner,
      R,
      A,
      C,
      I,
    };
    if (editingId) {
      setProcesses((prev) =>
        prev.map((p) => (p.id === editingId ? newProcess : p))
      );
    } else {
      setProcesses((prev) => [...prev, newProcess]);
    }
    setShowModal(false);
    setFormData(initialFormData);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData(initialFormData);
  };

  const handleExport = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [
        "Process,Departments,Task,Owner,R,A,C,I",
      ].concat(
        processes.map(
          (p) =>
            `"${p.processName.replace(/"/g, '""')}","${p.departments.join("|")}",${p.task.replace(/"/g, '""')},${p.owner},${p.R},${p.A},${p.C},${p.I}`
        )
      ).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "flexo_label_printing_raci.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtering
  const filtered = processes.filter((p) =>
    p.processName.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            🎛️ Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">
            Responsibilty Assignment Matrix / RACI Playground
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">
                {userName}
              </div>

              <button
                onClick={handleLogout}
                className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm"
            >
              🔐
            </button>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* ---------------- MOBILE MENU ---------------- */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs lg:text-sm">
                {userName}
              </div>

              <button
                onClick={handleLogout}
                className="p-2 rounded-md bg-red-600"
              >
                ⏻
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <Link to="/login" className="text-teal-300">
                🔐
              </Link>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-row items-center justify-between mb-4 gap-2">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 place-items-center mt-4">
          <button
            onClick={handlePopulate}
            className="text-xs lg:text-sm w-32 lg:w-48 px-1 py-1 bg-green-600 hover:bg-green-700 rounded"
          >
            <FontAwesomeIcon icon={faPlus} /> Populate Example
          </button>
          <button
            onClick={handleWhyRACI}
            className="text-xs lg:text-sm w-32 lg:w-40 px-1 py-1 bg-green-600 hover:bg-green-700 rounded"
          >
            <FontAwesomeIcon icon={faQuestionCircle} /> Why RACI?
          </button>
          <button
            onClick={handleAdd}
            className="text-xs lg:text-sm w-32 lg:w-40 px-1 py-1 bg-green-600 hover:bg-green-700 rounded"
          >
            <FontAwesomeIcon icon={faPlus} /> Add Process
          </button>
          <button
            onClick={handleExport}
            className="text-xs lg:text-sm w-32 lg:w-40 px-1 py-1 bg-blue-600 hover:bg-blue-700 rounded"
          >
            <FontAwesomeIcon icon={faDownload} /> Export CSV
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search Process..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full sm:w-1/2 px-3 py-2 rounded bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Desktop Table */}
      <div className="min-h-[40vh] hidden sm:block overflow-x-auto">
        <table className="min-w-full border border-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-3 py-2 text-left border-b border-gray-700">
                Process
              </th>
              <th className="px-3 py-2 text-left border-b border-gray-700">
                Departments
              </th>
              <th className="px-3 py-2 text-left border-b border-gray-700">Task</th>
              <th className="px-3 py-2 text-left border-b border-gray-700">Owner</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">R</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">A</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">C</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">I</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((p) => (
              <tr key={p.id} className="hover:bg-black">
                <td className="text-xs px-1 py-1 border-b border-gray-700">
                  {p.processName}
                </td>
                <td className="px-3 py-2 border-b border-gray-700">
                  <div className="flex flex-wrap gap-1">
                    {p.departments.map((d, idx) => (
                      <span
                        key={idx}
                        className="bg-purple-600 px-1 py-0.5 rounded-full text-xs"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-1 py-1 border-b border-gray-700">{p.task}</td>
                <td className="px-1 py-1 border-b border-gray-700">{p.owner}</td>
                <td className="px-1 py-1 border-b border-gray-700 text-center">
                  <span className={`px-1 py-0.5 rounded-sm text-xs ${raciColors.R}`}>
                    {p.R}
                  </span>
                </td>
                <td className="px-1 py-1 border-b border-gray-700 text-center">
                  <span className={`px-1 py-0.5 rounded-sm text-xs ${raciColors.A}`}>
                    {p.A}
                  </span>
                </td>
                <td className="px-1 py-1 border-b border-gray-700 text-center">
                  <span className={`px-1 py-0.5 rounded-sm text-xs ${raciColors.C}`}>
                    {p.C}
                  </span>
                </td>
                <td className="px-1 py-1 border-b border-gray-700 text-center">
                  <span className={`px-1 py-0.5 rounded-sm text-xs ${raciColors.I}`}>
                    {p.I}
                  </span>
                </td>
                <td className="px-1 py-1 border-b border-gray-700 flex justify-center gap-2">
                  <button
                    onClick={() => handleEdit(p.id)}
                    className="w-8 p-1 bg-yellow-600 hover:bg-yellow-700 rounded"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="w-8 p-1 bg-red-600 hover:bg-red-700 rounded"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr>
                <td
                  colSpan="9"
                  className="px-3 py-4 text-center text-gray-400"
                >
                  No processes found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden mt-4 space-y-4">
        {paginated.map((p) => (
          <div
            key={p.id}
            className="bg-gray-800 p-4 rounded shadow border border-gray-700"
          >
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-bold">{p.processName}</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(p.id)}
                  className="p-1 bg-yellow-600 hover:bg-yellow-700 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="p-1 bg-red-600 hover:bg-red-700 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {p.departments.map((d, idx) => (
                <span
                  key={idx}
                  className="bg-purple-600 px-2 py-0.5 rounded-full text-xs"
                >
                  {d}
                </span>
              ))}
            </div>
            <p>
              <span className="font-semibold">Task:</span> {p.task}
            </p>
            <p>
              <span className="font-semibold">Owner:</span> {p.owner}
            </p>
            <div className="flex justify-between mt-2 text-sm">
              <span className={`px-2 py-0.5 rounded ${raciColors.R}`}>
                R: {p.R}
              </span>
              <span className={`px-2 py-0.5 rounded ${raciColors.A}`}>
                A: {p.A}
              </span>
              <span className={`px-2 py-0.5 rounded ${raciColors.C}`}>
                C: {p.C}
              </span>
              <span className={`px-2 py-0.5 rounded ${raciColors.I}`}>
                I: {p.I}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Modal - Add / Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">
                {editingId ? "Edit Process" : "Add Process"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Process Name
                  </label>
                  <input
                    type="text"
                    value={formData.processName}
                    onChange={(e) =>
                      setFormData({ ...formData, processName: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Departments (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formData.departments}
                    onChange={(e) =>
                      setFormData({ ...formData, departments: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Task</label>
                  <input
                    type="text"
                    value={formData.task}
                    onChange={(e) =>
                      setFormData({ ...formData, task: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Owner</label>
                  <input
                    type="text"
                    value={formData.owner}
                    onChange={(e) =>
                      setFormData({ ...formData, owner: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium mb-1">R</label>
                    <input
                      type="text"
                      value={formData.R}
                      onChange={(e) =>
                        setFormData({ ...formData, R: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">A</label>
                    <input
                      type="text"
                      value={formData.A}
                      onChange={(e) =>
                        setFormData({ ...formData, A: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">C</label>
                    <input
                      type="text"
                      value={formData.C}
                      onChange={(e) =>
                        setFormData({ ...formData, C: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">I</label>
                    <input
                      type="text"
                      value={formData.I}
                      onChange={(e) =>
                        setFormData({ ...formData, I: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded"
                  >
                    {editingId ? "Update" : "Add"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Why RACI Modal */}
      {showWhyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-yellow-200">
                Why use RACI in Label Printing & Packaging?
              </h2>
              <button
                onClick={() => setShowWhyModal(false)}
                className="text-gray-400 hover:text-white text-2xl leading-none"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="space-y-4 text-sm leading-relaxed">
              <p>
                In flexographic / adhesive label printing factories — especially in MSMEs — jobs move quickly through many hands:
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Artwork → Prepress → Ink mixing → Press setup → Printing → Inspection → Packing</li>
                <li>Multiple shifts, small teams, people often handling 2–3 roles</li>
                <li>Very short make-ready times and high customer expectations</li>
              </ul>

              <p className="font-medium mt-4">Common problems without clear roles:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-red-300">
                <li>Who approves final color proof? → delay in production</li>
                <li>Who is responsible if register is off? → finger pointing</li>
                <li>Who should be informed about a rejection? → customer complaint</li>
              </ul>

              <p className="font-medium mt-4">RACI brings clarity:</p>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="bg-gray-700 p-3 rounded">
                  <span className="font-bold text-green-400">R</span>esponsible<br/>
                  <span className="text-xs text-gray-400">Does the work</span>
                </div>
                <div className="bg-gray-700 p-3 rounded">
                  <span className="font-bold text-blue-400">A</span>ccountable<br/>
                  <span className="text-xs text-gray-400">Owns the outcome</span>
                </div>
                <div className="bg-gray-700 p-3 rounded">
                  <span className="font-bold text-yellow-400">C</span>onsulted<br/>
                  <span className="text-xs text-gray-400">Must be asked before</span>
                </div>
                <div className="bg-gray-700 p-3 rounded">
                  <span className="font-bold text-gray-400">I</span>nformed<br/>
                  <span className="text-xs text-gray-400">Kept in the loop</span>
                </div>
              </div>

              <p className="mt-4 text-teal-300 font-medium">
                Result: faster job turnaround, fewer quality escapes, better audit readiness, and reduced stress in high-pressure production.
              </p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowWhyModal(false)}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      <div className="flex justify-center items-center gap-2 mt-6">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faChevronLeft} /> Prev
        </button>
        <span>Page {currentPage} / {totalPages || 1}</span>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-12">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudioRACI;